<template>
  <div
    ref="dialog"
    class="message-dialog"
    :class="classObject"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="id"
    @click.self="emitClose"
  >
    <div v-show="shown" tabindex="0"></div>
    <div ref="window" class="message-dialog__window">
      <!-- eslint-disable-next-line prettier/prettier -->
      <div class="message-dialog__header">
        <h2 :id="id">{{ unescapedTitle }}</h2>
        <div class="message-dialog__cross">
          <slot name="cross" />
        </div>
      </div>
      <div v-if="hasBody" ref="body" class="message-dialog__body">
        <slot name="body" />
        <!-- eslint-disable-next-line prettier/prettier -->
        <p v-if="text && !slots.body" ref="textRef" class="message-dialog__body-text" >{{ unescapedText }}</p>
      </div>
      <div v-if="slots.footer" class="message-dialog__footer">
        <slot name="footer" />
      </div>
    </div>
    <div v-show="shown" tabindex="0"></div>
  </div>
</template>

<script setup>
import { nanoid } from 'nanoid'

const { $isSp } = useNuxtApp()

const props = defineProps(
  {
    /**
     * タイトル
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * 本文
     */
    text: {
      type: String,
      default: null,
    },
    /**
     * 種類
     * @values reqular, alert, product
     */
    type: {
      type: String,
      default: 'regular',
    },
    /**
     * 表示状態
     */
    shown: {
      type: Boolean,
      default: false,
    },
    /**
     * 真ん中に寄せるかどうか
     */
    centered: {
      type: Boolean,
      default: false,
    },
    /**
     * borderあるかどうか
     */
    border: {
      type: Boolean,
      default: false,
    },
  }
)

const emit = defineEmits(['close'])

const slots = useSlots()

const id = ref(`dialog-label-${nanoid()}`)
const ignoreFocusChanges = ref(false)
const lastFocus = ref(null)
const modalLoaded = ref(false)
const dialog = ref(null)
const window = ref(null)
const body = ref(null)
const textRef = ref(null)


const classObject = computed(() => {
  return {
    hidden: !props.shown,
    alert: props.type === 'alert',
    product: props.type === 'product',
    centered: props.centered,
    border: props.border,
  }
})

const unescapedTitle = computed(() => {
  return props.title.replace(/\\n/g, '\n')
})

const unescapedText = computed(() => {
  return props.text.replace(/\\n/g, '\n')
})

const hasBody = computed(() => {
  return !!slots.body || props.text
})

const addEventListeners = () => {
  document.addEventListener('focus', trapFocus, true)
  document.addEventListener('touchmove', touchmoveFunction, {
    passive: false,
  })
  const interval = setInterval(() => {
    document
      .querySelector('.message-dialog__body')
      ?.addEventListener('scroll', checkHeight)
    if (modalLoaded.value) {
      clearInterval(interval)
    }
  }, 400)
}

const removeEventListeners = () => {
  document.removeEventListener('focus', trapFocus, true)
  document.removeEventListener('touchmove', touchmoveFunction, {
    passive: false,
  })
  document
    .querySelector('.message-dialog__body')
    ?.removeEventListener('scroll', checkHeight)
}

const isFocusable = (element) => {
  if (
    element.tabIndex > 0 ||
    (element.tabIndex === 0 && element.getAttribute('tabIndex') !== null)
  ) {
    return true
  }
  if (element.disabled) {
    return false
  }
  switch (element.nodeName) {
    case 'A':
      return !!element.href && element.rel !== 'ignore'
    case 'INPUT':
      return element.type !== 'hidden' && element.type !== 'file'
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true
    default:
      return false
  }
}

const initFocus = () => {
  if (props.shown) {
    focusFirstDescendant(window.value)
    lastFocus.value = document.activeElement
  }
}

const trapFocus = (event) => {
  if (ignoreFocusChanges.value) {
    return false
  }
  if (window.value?.contains(event.target)) {
    lastFocus.value = event.target
  } else {
    focusFirstDescendant(window.value)
    if (lastFocus.value === document.activeElement) {
      focusLastDescendant(window.value)
    }
    lastFocus.value = document.activeElement
  }
}

const touchmoveFunction = (e) => {
  const menu = document.querySelector('.message-dialog__body')
  const scrollable = menu.scrollHeight > menu.clientHeight
  const notTopOrBottom =
    menu.scrollTop !== 0 &&
    menu.scrollTop + menu.clientHeight !== menu.scrollHeight
  if (menu.contains(e.target) && scrollable && notTopOrBottom) {
    e.stopPropagation()
  } else {
    e.preventDefault()
  }
}

const focusFirstDescendant = (element) => {
  for (let i = 0; i < element.childNodes.length; i++) {
    const child = element.childNodes[i]
    if (attemptFocus(child) || focusFirstDescendant(child)) {
      return true
    }
  }
}

const focusLastDescendant = (element) => {
  for (let i = element.childNodes.length - 1; i >= 0; i--) {
    const child = element.childNodes[i]
    if (attemptFocus(child) || focusLastDescendant(child)) {
      return true
    }
  }
  return false
}

const attemptFocus = (element) => {
  // TODO: 有効にすると動作がおかしくなるのを解決（フォーカスが囲い込まれない・最初の要素にフォーカスが当たらない）
  // if (this.isFocusable(element)) {
  //   return false
  // }
  ignoreFocusChanges.value = true
  try {
    if (element) {
      element.focus()
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
  }
  ignoreFocusChanges.value = false
  return document.activeElement === element
}

const emitClose = () => {
  if (!$isSp(window.innerWidth)) {
    emit('close')
  }
}

const checkHeight = () => {
  const menu = document?.querySelector('.message-dialog__body')
  if (menu) {
    if (menu.scrollTop === 0) {
      menu.scrollTop = 1
    } else if (menu.scrollTop + menu.clientHeight === menu.scrollHeight) {
      menu.scrollTop = menu.scrollTop - 1
    }
  }
}

const onClose = () => {
  removeEventListeners()
  document.body.classList.remove('modal-dialog-shown')
  document.body.style = ''
  modalLoaded.value = false
}

watch(() => props.shown, (newValue, oldValue) => {
  const body = document.getElementsByTagName('body')[0]
  if (props.shown) {
    addEventListeners()
    body.classList.add('modal-dialog-shown')
    body.style = 'overflow: hidden;'
    nextTick(() => {
      checkHeight()
      modalLoaded.value = true
      if (
        props.text &&
        textRef.value?.clientHeight > body.value?.clientHeight
      ) {
        dialog.value?.classList.add('border')
      }
    })
  } else {
    onClose()
  }
}, {immediate: true})


onMounted(() => {
  initFocus()
})

onBeforeUnmount(() => {
  onClose()
})
</script>

<style lang="scss">
body {
  &.modal-dialog-shown {
    overflow: hidden;
  }
}
</style>

<style lang="scss" scoped>
$r: 0.4rem;

.message-dialog {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  animation: fade-in 0.3s;

  .message-dialog__window {
    border-radius: $r;
    margin: auto;
    width: 28rem;
    max-height: calc(100vh - 18.4rem);
    display: flex;
    flex-direction: column;
    background-color: $color-white;
    box-sizing: border-box;

    @media screen and (min-width: $tablet-breakpoint) {
      width: 50rem;
      position: relative;
      max-height: 65.4rem;
    }
  }

  .message-dialog__header {
    border-top-right-radius: $r;
    border-top-left-radius: $r;
    padding: 1.6rem 1.6rem 0.8rem;
    line-height: calc(23 / 16);
    font-size: 1.6rem;
    font-weight: 700;
    justify-content: space-between;
    align-items: center;
    @media screen and (min-width: $tablet-breakpoint) {
      white-space: pre-wrap;
      display: flex;
      font-size: 2rem;
      line-height: calc(28.96 / 20);
      border-bottom: 0.05rem solid $color-line;
    }

    .message-dialog__cross {
      display: none;
      @media screen and (min-width: $tablet-breakpoint) {
        display: block;
        margin-bottom: 0.8rem;
      }
    }

    > h2 {
      overflow-wrap: break-word;
    }
  }

  .message-dialog__body {
    margin: 1.6rem;
    flex: 1;
    overflow-y: auto;
    @media screen and (min-width: $tablet-breakpoint) {
      margin: 1.6rem 1.6rem 4rem;
    }
    // &::-webkit-scrollbar {
    //   width: 1.6rem;
    // }
  }

  .message-dialog__body-text {
    line-height: calc(21 / 14);
    font-size: 1.4rem;
    white-space: pre-wrap;
    color: $color-gray-text;
    @media screen and (min-width: $tablet-breakpoint) {
      font-size: 1.6rem;
      line-height: calc(23.17 / 16);
    }
  }

  .message-dialog__footer {
    padding: 1.6rem;

    @media screen and (min-width: $tablet-breakpoint) {
      padding: 0 10rem 2.4rem;
    }
  }

  &.hidden {
    display: none;
  }

  &.alert {
    .message-dialog__header {
      // background-color: $color-alert;
    }
  }

  &.product {
    .message-dialog__header {
      color: $color-white;
      background-color: $color-accent;
      border-bottom: none;
    }
    .message-dialog__body {
      margin: 0;
    }
    ::v-deep .point-benefits-item-target-products-list {
      border: none;
    }
  }

  &.border {
    .message-dialog__header {
      border-bottom: 0.05rem solid $color-line;
    }
    .message-dialog__footer {
      border-top: 0.05rem solid $color-line;
    }
  }

  &.centered {
    .message-dialog__header {
      justify-content: center;
      padding-bottom: 0;
      @media screen and (min-width: $tablet-breakpoint) {
        justify-content: space-between;
        border-bottom: 0.05rem solid $color-line;
        padding: 1.6rem 1.6rem 0.8rem;
      }
    }
  }
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }

  30% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}
</style>